<template>
  <section class="order-list h-100">
    <div
      v-if="showHeader"
      class="order-list__header d-flex justify-content-between align-items-start"
    >
      <div>
        <h5>{{ title }}</h5>
        <p v-if="description" class="text-muted mb-3">{{ description }}</p>
      </div>
      <b-button
        v-if="showSave"
        variant="primary"
        :disabled="loading || saving"
        @click="$emit('save')"
      >
        <b-spinner v-if="saving" small />
        <span v-else>Сохранить</span>
      </b-button>
    </div>
    <list-skeleton v-if="loading" :rows="5" :columns="2" />
    <b-alert v-else-if="!items.length" show variant="light" class="mb-0">
      Нет записей для сортировки
    </b-alert>
    <b-list-group v-else>
      <template v-for="(item, index) in items">
        <b-list-group-item
          v-if="visibleLimit && index === visibleLimit"
          :key="`limit-${item.id}`"
          class="display-limit text-center"
        >
          В мобильном приложении показываются только первые {{ visibleLimit }}
        </b-list-group-item>
        <b-list-group-item
          :key="item.id"
          draggable="true"
          class="order-item d-flex align-items-center"
          @dragstart="onDragStart($event, item)"
          @dragover.prevent="onDragOver"
          @drop="onDrop($event, item)"
          @dragend="removeDropIndicator"
        >
          <span class="drag-handle mr-2" title="Перетащить">
            <b-iconstack font-scale="1">
              <b-icon stacked icon="three-dots-vertical" />
              <b-icon stacked icon="three-dots-vertical" shift-h="4" />
            </b-iconstack>
          </span>
          <span class="order-number mr-3">{{ index + 1 }}</span>
          <span class="flex-grow-1">{{ item.title }}</span>
          <button
            v-if="showEdit"
            type="button"
            class="action-button action-edit"
            title="Изменить"
            @click="$emit('edit', item)"
          >
            <b-icon class="action-icon" icon="pen" />
            <span>Изменить</span>
          </button>
        </b-list-group-item>
      </template>
    </b-list-group>
  </section>
</template>

<script>
import ListSkeleton from '@/components/ListSkeleton.vue';

export default {
  name: 'OrderList',
  components: { ListSkeleton },
  props: {
    title: { type: String, required: true },
    description: { type: String, required: true },
    items: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    visibleLimit: { type: Number, default: null },
    showEdit: { type: Boolean, default: true },
    showHeader: { type: Boolean, default: true },
    showSave: { type: Boolean, default: true },
  },
  methods: {
    onDragStart(event, item) {
      event.dataTransfer.setData('text/plain', String(item.id));
      event.dataTransfer.effectAllowed = 'move';
    },
    onDragOver(event) {
      event.dataTransfer.dropEffect = 'move';
      this.removeDropIndicator();

      const placement = this.getDropPlacement(event);
      const indicator = document.createElement('div');
      indicator.id = 'order-list-drop-indicator';
      indicator.className = 'order-list-drop-indicator';

      const target = event.currentTarget;
      if (placement === 'before') {
        target.parentElement.insertBefore(indicator, target);
      } else {
        target.parentElement.insertBefore(indicator, target.nextElementSibling);
      }
    },
    onDrop(event, dropItem) {
      const movedItemId = Number(event.dataTransfer.getData('text/plain'));
      const placement = this.getDropPlacement(event);

      this.$emit('move', {
        movedItemId,
        dropItemId: dropItem.id,
        placement,
      });
      this.removeDropIndicator();
    },
    getDropPlacement(event) {
      const { top, bottom } = event.currentTarget.getBoundingClientRect();
      return event.clientY - top < bottom - event.clientY ? 'before' : 'after';
    },
    removeDropIndicator() {
      document.getElementById('order-list-drop-indicator')?.remove();
    },
  },
};
</script>

<style lang="scss" scoped>
.order-number {
  width: 28px;
  color: #6c757d;
  font-weight: 600;
  text-align: center;
}

.order-list__header {
  gap: 24px;
}

.display-limit {
  border-top: 3px solid #dc3545;
  color: #dc3545;
  font-weight: 600;
}

.order-item {
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }

  &:hover .drag-handle {
    opacity: 1;
  }
}

.drag-handle {
  color: #d40302;
  opacity: 0.45;
  transition: opacity 0.1s linear;
}

::v-deep .order-list-drop-indicator {
  width: 100%;
  height: 5px;
  background-color: #d40302;
}
</style>
